import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { colors, fonts, frames, glyphs } from '../../../Styles'
import { currentEsfViewTypes, useEsfContext } from '../_EsfWalkThrough/EsfContext';
import ESF_STATION from './EsfStation';
import ProgressBar from '../../ProgressViewer/progressBar';
import { Location, Section } from '../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getLfStructureCall';
import { useAppDispatch, useAppSelector } from '../../../Store';
import { dispatchFetchStation, selectStationByLocationId, selectStationMaxGridSpans } from '../../../Services/Agrisys_GqlDataCaller/StationCalls/StationSlice';
import { isEqual } from 'lodash';
import { Station } from '../../../Services/Agrisys_GqlDataCaller/StationCalls/queryStations';
import { StationLoading } from '..';
import { useAnimalConsumptionByMultipleIds } from '../../../Services/Agrisys_GqlDataCaller/ConsumptionCalls/consumptionSlice';
import { useFeedByLocation, useMultipleConsumptions } from '../../_Components/consumption';

interface lfPenslist_overrides {
  primaryColor?: colors;
  textColor?: colors;
  fittedWidth?: boolean;
  border?: string;
  attention?: number
}

interface lf_penlist_Props {
  focus_view?: boolean;
  location: Location
  // section: Section
  popoverContent?: (index: number, station: Station) => JSX.Element;
  overrides?: lfPenslist_overrides | ((index: number) => lfPenslist_overrides);
}
/**
 * SECTION_BASIC
 * @param sectionId Unique identifier for each section.
 * @param sectionType Basic, advanced and single. Each renders different content of animal data.
 * @param onPenClick OnClick event to handle penId.
 * @param onAddBtn OnClick event to handle Add btn.
 * @param onMoveBtn OnClick event to handle Move btn.
 * @param onEditBtn OnClick event to handle Edit btn.
 * @returns a section of pens for Freeda Location Feeder systems.
 */
export default function ESF_STATIONLIST({ focus_view, location, popoverContent, overrides }: lf_penlist_Props) {
  const { t } = useTranslation()
  const { state: { currentStationViewType } } = useEsfContext();
  const _framed = currentStationViewType !== currentEsfViewTypes.systemControle

  const dispatch = useAppDispatch();
  const maxRowsAndCols = useAppSelector(state => selectStationMaxGridSpans(state, location.location_id), isEqual)

  const { today: todayFeed, yesterday: yesterdayFeed } = useFeedByLocation(location.location_id, 5 * 60 * 1000);

  useEffect(() => {
    for (const id of location?.stations ?? []) {
      dispatch(dispatchFetchStation(id));
    }
  }, [dispatch, location?.stations]);

  const stations = useAppSelector((state) => selectStationByLocationId(state, location.location_id), isEqual);

  // Fallback
  if (stations.length === 0) {
    return <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, opacity: "0.3", fontStyle: "italic" }}>{t("noData", { defaultValue: "no data" })}</p>;
  }

  const _gridTemplateColumns = `repeat(${maxRowsAndCols.cols}, 1fr)`
  const _gridTemplateRows = `repeat(${maxRowsAndCols.rows}, 1fr)`
  // #endregion

  // #region | Single esf station
  const renderContent = () => {
    return stations.map((station, index) => {

      const _overrides = typeof overrides === "function" ? overrides(index) : overrides ?? {};
      const _popoverContent = typeof popoverContent === "function" ? popoverContent(index, station) : popoverContent ?? undefined;

      return (
        <ESF_STATION
          focus_view={focus_view}
          key={station.station_id}
          stationId={station.station_id}
          index={index}
          popoverContent={_popoverContent}
          gridArea={"THIS IS NOT USED THE COMPONENT SHOULD DO THIS ITSELF"}
          overrides={{
            primaryColor: _overrides.primaryColor,
            textColor: _overrides.textColor,
            width: _overrides.fittedWidth && maxRowsAndCols.cols === 4 ? "56px" : undefined,
            border: _overrides.border,
            attention: _overrides.attention
          }}
        />
      )
    });
  };
  // #endregion

  return (
    <frames.SectionFrame key={location?.location_id} $isHovered={_framed} style={{ padding: _framed ? "10px 12px 14px 12px" : "0px" }}>
      {_framed ? <p style={{ ...fonts.header, ...fonts.fontSize3, color: colors.greyDarkPrimary, zIndex: "2" }}>{location?.name}</p> : undefined}
      <div style={{ zIndex: "2", height: "15px", width: "117px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {(currentStationViewType === currentEsfViewTypes.remainingToday || currentStationViewType === currentEsfViewTypes.remainingYesterday) &&
          <ProgressBar
            total={currentStationViewType === currentEsfViewTypes.remainingToday ? todayFeed?.total
              : currentStationViewType === currentEsfViewTypes.remainingYesterday ? yesterdayFeed?.total
                : 0}
            progressed={currentStationViewType === currentEsfViewTypes.remainingToday ? yesterdayFeed?.progressed
              : currentStationViewType === currentEsfViewTypes.remainingYesterday ? yesterdayFeed?.progressed
                : 0
            }
          />
        }
      </div>
      <div style={{ display: "grid", gridTemplateColumns: _gridTemplateColumns, gridTemplateRows: _gridTemplateRows, gap: "10px", zIndex: "2" }}>
        {renderContent()}
      </div>

      {_framed ? <div style={{ position: "absolute", inset: "0 0 0 0", backgroundColor: colors.greyLightSecondary + "bf", borderRadius: "10px", zIndex: "1" }} /> : undefined}
    </frames.SectionFrame>
  );
}
